import React from "react";
import { Alert, Box, List, Stack } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { ClientNote } from "../../types/client";

interface PopupNotesAlertProps {
  /** All notes on the client; this component filters for is_popup === true. */
  notes?: ClientNote[];
  /**
   * Visual density. "default" is the full-size alert used on the client page;
   * "compact" tightens padding and type for dense surfaces like the
   * transaction modal. "soft" is the modern soft-panel look used by the
   * redesigned transaction modal — same notes, calmer surface.
   */
  variant?: "default" | "compact" | "soft";
}

/**
 * Prominent alert surfaced on the client page and on any transaction tied to
 * the client when there are popup-flagged client notes. Renders nothing if
 * there's nothing to surface.
 */
const PopupNotesAlert: React.FC<PopupNotesAlertProps> = ({
  notes,
  variant = "default",
}) => {
  const popupNotes = (notes || []).filter((n) => n.is_popup === true);

  if (popupNotes.length === 0) {
    return null;
  }

  if (variant === "soft") {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "12px 16px",
          background: "rgba(222, 133, 78, 0.12)",
          borderRadius: 12,
          color: "var(--color-accent-active)",
          fontSize: 13.5,
          lineHeight: 1.5,
        }}
      >
        <IconAlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2 }} />
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          {popupNotes.map((n) => (
            <Box key={n.id}>{n.text}</Box>
          ))}
        </Stack>
      </Box>
    );
  }

  const isCompact = variant === "compact";
  const textSize = isCompact ? "xs" : "sm";

  return (
    <Alert
      color="var(--brand-burnt-orange)"
      title="Important notes"
      icon={<IconAlertTriangle size={isCompact ? 16 : 20} />}
      radius="md"
      variant="filled"
      withCloseButton={false}
      mt="xs"
      p={isCompact ? "xs" : undefined}
      styles={
        isCompact
          ? {
              title: {
                fontSize: "var(--mantine-font-size-xs)",
                marginBottom: 2,
              },
            }
          : undefined
      }
    >
      <Stack gap={isCompact ? 2 : 4}>
        <List size={textSize} c="white">
          {popupNotes.map((n) => (
            <List.Item key={n.id}>{n.text}</List.Item>
          ))}
        </List>
      </Stack>
    </Alert>
  );
};

export default PopupNotesAlert;
